/**
 * DomainAgentChips — shows, above the chat composer, which domain agents the conversation talks to.
 * Selected agents are chips (× removes); with none selected no domain agent is active. "+ Domain agent"
 * adds a connected one. Renders nothing when the user has no connected domain agent.
 */
import React, { useState } from 'react';
import { Plug, Plus, X } from 'lucide-react';

// Same palette as the dashboard's T2 (kept local: RunDrawer renders this and must not import the app shell).
const C = { border: 'var(--lyra-color-border)', surface: 'var(--lyra-color-surface)', ink2: 'var(--app-text-2)', ink3: 'var(--lyra-color-text-muted)', accentSoft: 'var(--app-primary-soft)', accentText: 'var(--app-primary-deep)' };

type Agent = { id: string; name: string };

export default function DomainAgentChips({ selected, available, onAdd, onRemove }: {
  selected: Agent[];
  available: Agent[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (selected.length === 0 && available.length === 0) return null;

  return (
    <div data-testid="domain-agent-chips" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '8px 12px 0', position: 'relative' }}>
      <span style={{ fontSize: 11, color: C.ink3, fontWeight: 600 }}>{selected.length ? 'Talking to' : 'Domain agents: None selected'}</span>
      {selected.map((a) => (
        <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 6px 3px 8px', borderRadius: 999, background: C.accentSoft, color: C.accentText, fontSize: 12, fontWeight: 600 }}>
          <Plug size={11} /> {a.name}
          <button aria-label={`Remove ${a.name}`} onMouseDown={(e) => { e.preventDefault(); onRemove(a.id); }} style={{ display: 'flex', alignItems: 'center', border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: C.accentText, opacity: 0.7 }}>
            <X size={12} />
          </button>
        </span>
      ))}
      {available.length > 0 && (
        <span style={{ position: 'relative' }}>
          <button onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px dashed ${C.border}`, borderRadius: 999, padding: '3px 9px', background: 'transparent', color: C.ink2, fontSize: 12, cursor: 'pointer' }}>
            <Plus size={11} /> Domain agent
          </button>
          {open && (
            <div role="menu" style={{ position: 'absolute', zIndex: 20, top: '110%', left: 0, minWidth: 180, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4, boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }}>
              {available.map((a) => (
                <button key={a.id} role="menuitem" onClick={() => { onAdd(a.id); setOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '7px 10px', fontSize: 12.5, color: C.ink2, cursor: 'pointer', borderRadius: 7 }}>
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
}
